/*
 * analyticsProfiles.js — Analytics Profile registry (Task D).
 *
 * Defines per-collection-type analytics profiles consumed by the Dashboard.
 * Each profile is a pure data object — no JSX, no API calls, no side
 * effects. Consumers (Dashboard) read the profile that matches the
 * currently selected collection's `type_id` and render KPI / chart
 * groupings accordingly.
 *
 * Two profile sections per type:
 *   • basicCharts   — always visible (free tier).
 *   • premiumCharts — visible but locked behind a Premium overlay
 *                     unless `user.isPremium` is true.
 *
 * Field mappings always point to the JSON path inside `item.item_data` so
 * the Dashboard can read values without any backend change. If the field
 * does not exist on any item in the current scope, the chart card hides
 * itself gracefully (handled in Dashboard render layer).
 *
 * Schema (per profile):
 *   id           string  — matches a `COLLECTION_TYPES.id` from
 *                          collectionTypes.js (or "generic" for fallback).
 *   itemLabel    string  — singular noun used in KPI titles
 *                          ("Bottle", "Coin", "Item"…).
 *   itemLabelPlural string — plural form ("Bottles", "Coins"…).
 *   basicCharts  Array<ChartSpec> — universal chart set.
 *   premiumCharts Array<ChartSpec> — locked per-type chart set.
 *
 * ChartSpec:
 *   id     string  — stable key for React lists / lock overlay copy.
 *   title  string  — human label shown in the chart header.
 *   kind   string  — "category" | "dimension" | "timeline" | "histogram"
 *                    | "pie" | "value-over-time"
 *   field  string? — optional `item_data` field name (for dimension /
 *                    histogram / pie kinds). Omitted for kinds that read
 *                    from canonical columns (timeline / category /
 *                    value-over-time).
 *
 * Adding a new type? Add an entry below and the Dashboard will render the
 * basic + locked premium cards automatically — no Dashboard edits needed.
 */

// ---- Universal basic chart set ------------------------------------------
// Every collection type gets these. They read canonical fields that exist
// on every Item document (cost, sell price, created_at, category_id) and
// therefore never need to be hidden.
const BASIC_CHARTS = [
  { id: "items-by-category", title: "Items by Category", kind: "category" },
  { id: "value-over-time",   title: "Value Over Time",   kind: "value-over-time" },
];

export const ANALYTICS_PROFILES = {
  // ---------- Core types ----------
  wine: {
    id: "wine",
    itemLabel: "Bottle",
    itemLabelPlural: "Bottles",
    basicCharts: BASIC_CHARTS,
    premiumCharts: [
      { id: "by-country",       title: "Bottles by Country",           kind: "dimension", field: "Country" },
      { id: "by-region",        title: "Bottles by Region",            kind: "dimension", field: "Region" },
      { id: "by-sub-region",    title: "Bottles by Sub-Region",        kind: "dimension", field: "Sub-Region" },
      { id: "by-grape",         title: "Bottles by Grape Variety",     kind: "dimension", field: "Grape(s)" },
      { id: "vintage-distribution", title: "Vintage Distribution",     kind: "histogram", field: "Vintage" },
      { id: "drinking-window",  title: "Drinking Window — Peak Timeline", kind: "timeline" },
      { id: "cellar-composition", title: "Cellar Composition",         kind: "pie",       field: "Wine Type" },
      { id: "region-maturity",  title: "Region Maturity Curve",        kind: "dimension", field: "Region" },
    ],
  },

  whisky: {
    id: "whisky",
    itemLabel: "Bottle",
    itemLabelPlural: "Bottles",
    basicCharts: BASIC_CHARTS,
    premiumCharts: [
      { id: "by-distillery", title: "Bottles by Distillery", kind: "dimension", field: "Distillery" },
      { id: "by-country",    title: "Bottles by Country",    kind: "dimension", field: "Country" },
      { id: "by-region",     title: "Bottles by Region",     kind: "dimension", field: "Region" },
      { id: "by-age",        title: "Age Statement Distribution", kind: "histogram", field: "Age Statement" },
      { id: "by-cask",       title: "Bottles by Cask Type",  kind: "dimension", field: "Cask Type" },
      { id: "abv-distribution", title: "ABV Distribution",   kind: "histogram", field: "ABV %" },
    ],
  },

  spirits: {
    id: "spirits",
    itemLabel: "Bottle",
    itemLabelPlural: "Bottles",
    basicCharts: BASIC_CHARTS,
    premiumCharts: [
      { id: "by-brand",  title: "Bottles by Brand",         kind: "dimension", field: "Brand / Producer" },
      { id: "by-type",   title: "Bottles by Spirit Type",   kind: "dimension", field: "Spirit Type" },
      { id: "by-country", title: "Bottles by Country",      kind: "dimension", field: "Country" },
      { id: "abv-distribution", title: "ABV Distribution",  kind: "histogram", field: "ABV %" },
    ],
  },

  // ---------- Generic fallback ----------
  // Used when the collection's type_id is missing or unknown. All other
  // per-type profiles (beer, coins, watches, comics, …) were retired
  // 2026-02-26 alongside the collectionTypes.js pruning.
  generic: {
    id: "generic",
    itemLabel: "Item",
    itemLabelPlural: "Items",
    basicCharts: BASIC_CHARTS,
    premiumCharts: [], // No type-specific premium charts for generic.
  },
};

/**
 * Resolve the analytics profile for a given collection type id.
 * Falls back to the generic profile when the type is missing / unknown.
 */
export function getAnalyticsProfileForType(typeId) {
  if (!typeId) return ANALYTICS_PROFILES.generic;
  return ANALYTICS_PROFILES[typeId] || ANALYTICS_PROFILES.generic;
}
